/**
 * The capture entry tile. Mini PRD: "Capture tile on Care tab + Home."
 */
import { useNavigation } from '@/hooks/useNavigationContext';
import { Box, Stack, Typography } from '@mui/material';

import { Card } from '@karehero/llama/components/atoms/Card/Card';
import { Button } from '@karehero/llama/components/atoms/Button/Button';

import { CameraIcon, SparklesIcon } from './LocalIcons';
import { palette } from './palette';
import { useCaptureFlow, type EntrySurface } from '../CaptureFlowContext';

interface CaptureTileProps {
  /** Which entry point this tile lives on - feeds the `surface` property of
   * the Document Uploaded instrumentation event. */
  surface: EntrySurface;
}

export const CaptureTile = ({ surface }: CaptureTileProps) => {
  const navigate = useNavigation();
  const { setEntrySurface, track } = useCaptureFlow();

  return (
    <Card isFullWidth>
      <Stack sx={{ alignItems: 'flex-start' }} spacing={1.5}>
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            backgroundColor: palette.forest,
            color: palette.cream,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CameraIcon size={22} />
        </Box>
        <Typography variant='h6'>Show us a care document</Typography>
        <Typography sx={{ color: palette.mutedText, fontSize: 14 }}>
          Take a photo or upload a file. Keni will tell you what it means and
          what to do next.
        </Typography>
        <Stack
          direction='row'
          spacing={0.75}
          sx={{
            alignItems: 'center',
            backgroundColor: palette.lavender,
            color: palette.lavenderInk,
            borderRadius: 99,
            padding: '4px 10px',
          }}
        >
          <SparklesIcon size={13} />
          <Typography sx={{ fontSize: 12 }}>
            Keni extracts the details automatically
          </Typography>
        </Stack>
        <Button
          ariaLabel='Capture a document'
          variant='primary'
          isFullWidth
          onPress={() => {
            setEntrySurface(surface);
            track('Capture Entry Tapped (proposed)', { surface });
            navigate('/capture');
          }}
        >
          Capture a document
        </Button>
      </Stack>
    </Card>
  );
};

export default CaptureTile;
